import React, { useRef, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { ImagePlusIcon } from 'lucide-react';
import { Modal } from '../../../ui/Modal';
import { PillButton } from '../../../ui/PillButton';
import { ConfirmDialog } from '../../../ui/ConfirmDialog';
import { ProfileIcon } from '../../../icons/ProfileIcons';
import { IconPicker } from './IconPicker';
import type { ProfileIconId, SupportSettings } from '../../../../types/supportProfiles';
type SaveProfileModalProps = {
  settings: SupportSettings;
  onCancel: () => void;
  onSave: (name: string, icon: ProfileIconId) => void;
};
export function SaveProfileModal({
  settings,
  onCancel,
  onSave
}: SaveProfileModalProps) {
  const [name, setName] = useState('');
  const [icon, setIcon] = useState<ProfileIconId | null>(null);
  const [picker, setPicker] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const iconRef = useRef<HTMLButtonElement>(null);
  const trimmed = name.trim();
  const dirty = trimmed !== '' || icon !== null;
  const requestClose = () => dirty ? setConfirm(true) : onCancel();
  const save = (fallback = false) => {
    const finalName = trimmed || (fallback ? 'Untitled Profile' : '');
    if (finalName) onSave(finalName, icon ?? 'custom');
  };
  const enabledCount = [settings.minima, settings.overhangs, settings.stabilization, settings.reinforcements].filter(Boolean).length;
  const summary: [string, string][] = [['Touchpoint Size', `${settings.touchpointSize.toFixed(2)} mm`], ['Support Types', `${enabledCount} of 4 on`], ['Overhang Density', settings.overhangDensity.toFixed(2)], ['Overhang Angle', `${settings.overhangAngle}°`], ['Reinforcement Density', settings.reinforcementDensity.toFixed(2)]];
  return <Modal title="Save Support Profile" description="Save all current auto support settings so you can reuse them on other prints." onRequestClose={requestClose} suspendEscape={confirm} width={360} footer={<>
          <PillButton className="h-[28px] flex-none px-4" onClick={requestClose} data-id="element-579" data-mp="204j2gd00ezgz">
            Cancel
          </PillButton>
          <PillButton variant="primary" className="ml-auto h-[28px] flex-none px-5" disabled={!trimmed} onClick={() => save()} data-id="element-580" data-mp="252udgr21jg0s">
            Save Profile
          </PillButton>
        </>} overlay={<AnimatePresence data-id="element-581" data-mp="2etp407mbl1s1">
          {confirm && <ConfirmDialog title="Discard this profile?" message="Are you sure you don’t want to save this profile? Your changes will be lost." confirmLabel="Save Profile" discardLabel="Don’t Save" onCancel={() => setConfirm(false)} onDiscard={onCancel} onConfirm={() => save(true)} data-id="element-582" data-mp="2iuhbowfjmym7" />}
        </AnimatePresence>} data-id="element-578" data-mp="1j46skewbqq7v">
      
      <form onSubmit={e => {
      e.preventDefault();
      save();
    }} data-id="element-583" data-mp="yew4qgquhowo">
        
        <label htmlFor="profile-name" className="text-[12px] font-semibold text-pf-ink" data-id="element-584" data-mp="13e8qey5zcpxo">
          Icon & name
        </label>
        <div className="mt-[6px] flex items-center gap-[10px]" data-id="element-585" data-mp="3gp1v5x67ofh0">
          <button ref={iconRef} type="button" aria-label={icon ? 'Change profile icon' : 'Choose a profile icon'} aria-expanded={picker} onClick={() => setPicker(o => !o)} className={`flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-[8px] border transition-colors duration-150 ${picker ? 'border-pf-blue bg-pf-blue-soft text-pf-blue' : icon ? 'border-pf-line bg-white text-pf-ink hover:border-pf-blue-line' : 'border-dashed border-[#b9bec4] bg-pf-panel text-pf-muted hover:border-pf-blue hover:text-pf-blue'}`} data-id="element-586" data-mp="3bwidao0hqy6e">
            
            {icon ? <ProfileIcon icon={icon} className="h-[18px] w-[18px]" data-id="element-587" data-mp="2f6rjkr00xf1n" /> : <ImagePlusIcon className="h-[17px] w-[17px]" strokeWidth={2} data-id="element-588" data-mp="2f45507eptz9x" />}
          </button>
          <input id="profile-name" autoFocus value={name} maxLength={40} onChange={e => setName(e.target.value)} placeholder="e.g. Fine Detail Resin" className="h-[36px] min-w-0 flex-1 rounded-[7px] border border-pf-line bg-white px-[10px] text-[12.5px] text-pf-ink outline-none transition-colors duration-150 placeholder:text-pf-faint focus:border-pf-blue focus:ring-2 focus:ring-pf-blue/15" data-id="element-589" data-mp="2a9rfumqncodp" />
          
        </div>

        <div className="mt-[14px] rounded-[8px] bg-pf-panel px-[10px] py-[8px]" data-id="element-590" data-mp="lkuyycagspgw">
          <p className="text-[11px] font-semibold text-pf-muted" data-id="element-591" data-mp="pm6uszjxm6xj">Settings included</p>
          <dl className="mt-[4px] grid grid-cols-[1fr_auto] gap-x-3 gap-y-[3px] text-[11.5px]" data-id="element-592" data-mp="bo08mgochvdp">
            {summary.map(([k, v]) => <React.Fragment key={k} data-id="element-593" data-mp="2weqw6rildrky">
                <dt className="text-pf-muted" data-id="element-594" data-mp="2wdphmd2damyh">{k}</dt>
                <dd className="text-right tabular-nums text-pf-ink" data-id="element-595" data-mp="31du18byknxsx">{v}</dd>
              </React.Fragment>)}
          </dl>
        </div>
      </form>
      <IconPicker anchorRef={iconRef} open={picker} onClose={() => setPicker(false)} value={icon} onSelect={id => {
      setIcon(id);
      setPicker(false);
    }} data-id="element-596" data-mp="2tt0010v4yu9p" />
      
    </Modal>;
}